import { Eye, KeyRound, Plus, Trash2, Upload, UserIcon } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppButton from "../../components/ui/AppButton";
import DashboardFilterBar, {
  ASSIGNMENT_FILTER_OPTIONS,
  boolFromToggleFilter,
  filterSelectClass,
  isAllFilter,
  STATUS_FILTER_OPTIONS,
  stringFromFilter,
  TRIP_STATUS_FILTER_OPTIONS,
} from "../../components/ui/dashboard/DashboardFilterBar";
import Pagination from "../../components/ui/dashboard/Pagination";
import { CurrentLocation } from "../../components/ui/dashboard/StatusCommon";
import Table, { Column } from "../../components/ui/dashboard/Table";
import ThreeDotsMenu from "../../components/ui/ThreeDotsMenu";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { useResendCredentials } from "../../hooks/useResendCredentials";
import { buildApiUrl, cn } from "../../lib/utils";
import DeleteUserModal from "../user/ui/DeleteUserModal";
import VehicleTypesDropdown from "../vehicle/ui/VehicleTypesDropdown";
import { Driver, DriverSearchRequest, DriverSearchResponse } from "./@types";
import DriverBulkUploadModal from "./components/DriverBulkUploadModal";
import { useGetDriversMutation } from "./driverApiSlice";
import DriverModel from "./DriverModal";
import DashboardDriverStats from "./refactor/DriverDashStats";

const DriverDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [assignedFilter, setAssignedFilter] = useState("all");
  const [tripStatusFilter, setTripStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [openModal, setOpenModal] = useState("");

  const { resend: resendCredentials } = useResendCredentials();

  const debouncedKeyword = useDebouncedValue(searchTerm.trim());
  const [getDrivers, { data: driverData, isLoading, error: loadError }] = useGetDriversMutation();

  const fetchDrivers = useCallback(async () => {
    const requestBody: DriverSearchRequest = {
      pageNumber: currentPage,
      pageSize,
      keyword: debouncedKeyword || undefined,
      vehicleTypes: stringFromFilter(vehicleTypeFilter),
      tripStatus: stringFromFilter(tripStatusFilter),
      isAssigned: boolFromToggleFilter(
        assignedFilter,
        "assigned",
        "unassigned",
      ),
      isActive: boolFromToggleFilter(statusFilter, "active", "inactive"),
      orderBy: ["CreatedOn"],
    };

    try {
      await getDrivers(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch drivers:", err);
    }
  }, [
    currentPage,
    pageSize,
    debouncedKeyword,
    vehicleTypeFilter,
    statusFilter,
    assignedFilter,
    tripStatusFilter,
    getDrivers,
  ]);

  useEffect(() => {
    fetchDrivers();
  }, [fetchDrivers]);

  useEffect(() => {
    setCurrentPage(0);
  }, [
    debouncedKeyword,
    vehicleTypeFilter,
    statusFilter,
    assignedFilter,
    tripStatusFilter,
  ]);

  // Table columns configuration
  const columns: Column<Driver>[] = [
    {
      key: "userName",
      header: "Driver name",
      width: "",
      render: (value: string, item: Driver) => (
        <div className="flex items-center space-x-3">
          {item.imageUrl ? (
            <img
              src={buildApiUrl(item.imageUrl)}
              alt={`${item.fullName}`}
              className="w-[30px] h-[30px] rounded-full object-cover border border-gray-300 shadow-sm"
            />
          ) : (
            <div className="w-[30px] h-[30px] rounded-full bg-gray-200 flex items-center justify-center border border-gray-300 shadow-sm p-1">
              <UserIcon className="w-full h-full text-hColor" />
            </div>
          )}
          <div className="flex flex-col">
            <span className="text-hColor capitalize">{item.fullName}</span>
          </div>
        </div>
      ),
    },
    {
      key: "currentVehicle",
      header: "Assigned vehicle",

      render: (value: string) => (
        <span className="text-hColor capitalize">{value}</span>
      ),
    },
    {
      key: "currentVehicleType",
      header: "Vehicle type",

      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "totalDistance",
      header: "Distance covered",

      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "currentLocation",
      header: "Current location",

      render: (value: string) => <CurrentLocation location={value} />,
    },
    {
      key: "isActive",
      header: "Status",

      render: (value: boolean, item: Driver) => {
        const getStatusInfo = () => {
          if (!value)
            return {
              label: "Inactive",
              color: "text-gray-200 border border-gray-200",
            };
          if (item.isActive)
            return {
              label: "Active",
              color: "text-green-800 border border-green-800",
            };
          return {
            label: "Active",
            color: "text-green-800 border border-green-800",
          };
        };

        const status = getStatusInfo();
        return (
          <span
            className={`w-[60px] inline-flex items-center justify-center px-2 py-1 text-xs font-semibold rounded-full ${status.color}`}
          >
            {status.label}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "Action",

      align: "center" as const,
      render: (value: any, item: Driver) => {
        return (
          <ThreeDotsMenu
            actions={[
              {
                label: "View driver",
                onClick: () => navigate(`/admin/drivers/${item?.id}`),
                icon: <Eye className="w-4 h-4" />,
              },
              {
                label: "Resend credentials",
                onClick: () => resendCredentials(item.email),
                icon: <KeyRound className="w-4 h-4" />,
              },
              {
                label: "Delete driver",
                onClick: () => {
                  setSelectedDriver(item);
                  setOpenModal("delete");
                },
                icon: <Trash2 className="w-4 h-4" />,
              },
            ]}
          />
        );
      },
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const handleExportCSV = () => {
    console.log("Export CSV functionality");
  };

  const handleModal = () => {
    setOpenModal("");
    fetchDrivers();
  };

  const handleFilterChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (value: string) => {
      setter(value || "all");
    };

  const handleClearFilters = () => {
    setSearchTerm("");
    setVehicleTypeFilter("all");
    setStatusFilter("all");
    setAssignedFilter("all");
    setTripStatusFilter("all");
  };

  const activeFilters = useMemo(() => {
    const chips: { key: string; label: string; onRemove: () => void }[] = [];
    if (debouncedKeyword) {
      chips.push({
        key: "keyword",
        label: `Search: ${debouncedKeyword}`,
        onRemove: () => setSearchTerm(""),
      });
    }
    if (!isAllFilter(vehicleTypeFilter)) {
      chips.push({
        key: "type",
        label: `Type: ${vehicleTypeFilter}`,
        onRemove: () => setVehicleTypeFilter("all"),
      });
    }
    if (!isAllFilter(statusFilter)) {
      chips.push({
        key: "status",
        label: `Status: ${STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter)?.label}`,
        onRemove: () => setStatusFilter("all"),
      });
    }
    if (!isAllFilter(assignedFilter)) {
      chips.push({
        key: "assigned",
        label: `Assignment: ${ASSIGNMENT_FILTER_OPTIONS.find((o) => o.value === assignedFilter)?.label}`,
        onRemove: () => setAssignedFilter("all"),
      });
    }
    if (!isAllFilter(tripStatusFilter)) {
      chips.push({
        key: "trip",
        label: `Trip: ${TRIP_STATUS_FILTER_OPTIONS.find((o) => o.value === tripStatusFilter)?.label}`,
        onRemove: () => setTripStatusFilter("all"),
      });
    }
    return chips;
  }, [
    debouncedKeyword,
    vehicleTypeFilter,
    statusFilter,
    assignedFilter,
    tripStatusFilter,
  ]);

  const response = driverData?.data as DriverSearchResponse | undefined;

  return (
    <div className="min-h-screen py-6 w-full bg-[#F9F9F9] p-4">
      <div className="w-full mx-auto space-y-6">
        {/* Dashboard Cards */}
        <div className="">
          <DashboardDriverStats />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-[10px] p-4">
          <div className="">
            <h2 className="text-xl font-semibold text-hColor">All drivers</h2>
          </div>

          <div className="flex gap-3">
            <AppButton
              appearance="outline"
              onClick={() => setOpenModal("bulk")}
              className="flex items-center px-4 py-2 rounded-lg w-auto h-[38px]"
            >
              <Upload className="h-4 w-4 mr-2" />
              Bulk upload
            </AppButton>
            <AppButton
              onClick={() => setOpenModal("add")}
              className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/60 transition-colors min-w-[146px] h-[38px]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add driver
            </AppButton>
          </div>
        </div>

        {/* All drivers section */}
        <div className="space-y-4 bg-white rounded-[10px] p-4">
          <DashboardFilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onClearSearch={() => setSearchTerm("")}
            searchPlaceholder="Search by name, email or vehicle"
            isLoading={isLoading}
            extraFilters={
              <VehicleTypesDropdown
                showLabel={false}
                includeAll
                showSearch
                value={vehicleTypeFilter}
                onChange={handleFilterChange(setVehicleTypeFilter)}
                placeholder="Vehicle type"
                containerClassName="w-auto"
                className={cn(filterSelectClass, "w-full sm:w-[160px]")}
              />
            }
            filters={[
              {
                id: "status",
                placeholder: "Status",
                value: statusFilter,
                options: STATUS_FILTER_OPTIONS,
                onChange: handleFilterChange(setStatusFilter),
              },
              {
                id: "assignment",
                placeholder: "Assignment",
                value: assignedFilter,
                options: ASSIGNMENT_FILTER_OPTIONS,
                onChange: handleFilterChange(setAssignedFilter),
              },
              {
                id: "trip-status",
                placeholder: "Trip status",
                value: tripStatusFilter,
                options: TRIP_STATUS_FILTER_OPTIONS,
                onChange: handleFilterChange(setTripStatusFilter),
              },
            ]}
            chips={activeFilters}
            onClearAll={handleClearFilters}
            resultCount={response?.totalCount}
            resultLabel={response?.totalCount === 1 ? "driver" : "drivers"}
          />
            <Table
              data={response?.data || []}
              columns={columns}
              loading={isLoading}
              error={loadError}
              emptyMessage="No drivers found"
            />
        </div>
        <div className="">
          {/* Pagination */}
          {response && (
            <Pagination
              currentPage={response.currentPage}
              totalPages={response.totalPages}
              pageSize={response.pageSize}
              totalCount={response.totalCount}
              hasNextPage={response.hasNextPage}
              hasPreviousPage={response.hasPreviousPage}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </div>
      </div>

      <DriverModel
        isOpen={openModal === "add"}
        onClose={handleModal}
        driver={selectedDriver}
      />

      <DriverBulkUploadModal
        isOpen={openModal === "bulk"}
        onClose={handleModal}
      />

      <DeleteUserModal
        isOpen={openModal === "delete"}
        onClose={handleModal}
        userId={selectedDriver?.userId}
        entityLabel="driver"
      />
    </div>
  );
};

export default DriverDashboard;
